import { AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Badge, DataTable, EmptyState } from '@/components/ui';
import { formatRM } from '@/features/billing/money';
import type { PatientPanelUsageEntry } from '@/features/panel-limits/api';
import styles from './PanelLimits.module.css';

function limit(entry: PatientPanelUsageEntry) {
  return entry.annualCapSen === null
    ? 'No annual cap'
    : formatRM(entry.annualCapSen);
}

function remaining(entry: PatientPanelUsageEntry) {
  return entry.remainingAnnualSen === null
    ? 'No annual cap'
    : formatRM(entry.remainingAnnualSen);
}

export function PatientPanelUsageLedger({
  entries,
  loading,
  error,
  onRetry,
}: {
  entries?: PatientPanelUsageEntry[];
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <DataTable
      caption={t('panelLimits.ledgerTitle')}
      rows={entries}
      rowKey={(entry) => entry.id}
      loading={loading}
      error={error}
      onRetry={onRetry}
      empty={
        <EmptyState
          title={t('panelLimits.noUsage')}
          body={t('panelLimits.noUsageBody')}
        />
      }
      columns={[
        {
          key: 'patient',
          header: t('panelLimits.patient'),
          render: (entry) => (
            <div>
              <strong>{entry.patientName}</strong>
              <span className={styles.muted}>{entry.panelName}</span>
            </div>
          ),
        },
        {
          key: 'claim',
          header: t('panelLimits.claim'),
          render: (entry) => (
            <div>
              <strong>{entry.claimReference}</strong>
              <span className={styles.muted}>{entry.visitDate}</span>
            </div>
          ),
        },
        {
          key: 'used',
          header: t('panelLimits.claimUsage'),
          render: (entry) => formatRM(entry.claimAmountSen),
        },
        {
          key: 'annual',
          header: t('panelLimits.annualUsage'),
          render: (entry) => (
            <div>
              <strong>{formatRM(entry.annualUsedSen)}</strong>
              <span className={styles.muted}>
                {t('panelLimits.ofLimit', { limit: limit(entry) })}
              </span>
            </div>
          ),
        },
        {
          key: 'remaining',
          header: t('panelLimits.remaining'),
          render: remaining,
        },
        {
          key: 'status',
          header: t('panelLimits.status'),
          render: (entry) => (
            <Badge
              tone={
                entry.status === 'over'
                  ? 'danger'
                  : entry.status === 'near'
                    ? 'warning'
                    : 'success'
              }
              icon={
                entry.status !== 'ok' ? <AlertTriangle size={13} /> : undefined
              }
            >
              {t(`panelLimits.states.${entry.status}`, {
                threshold: entry.warningThresholdPercent,
              })}
            </Badge>
          ),
        },
      ]}
    />
  );
}
